import { useCorporate } from "@/context/CorporateGroupContext";
import { Company } from "@/interfaces/Company";
import { ConfigComun } from "@/interfaces/Comun";
import { setDriverData } from "@/stores/features/driverQuestionSlice";
import {
  setAuditCurrent,
  setNextAuditCurrent,
  setNextExternalCurrent,
  setPrevAuditCurrent,
} from "@/stores/features/utilsSlice";
import {
  syncChecklistIds,
  fullResetAudit,
  setCheckAuditDataForChanges,
} from "@/stores/features/auditSlice";
import { setFleetData } from "@/stores/features/vehicleQuestionsSlice";
import { useAppDispatch, useAppSelector } from "@/stores/hooks";
import { companyService } from "@/stores/services/companyService";
import { corporateGroupService } from "@/stores/services/corporateGroupService";
// import { diagnosisService } from "@/stores/services/diagnosisServices";
import { auditService } from "@/stores/services/auditService";
import { TOAST_TYPE, toastHandler } from "@/utils/useToast";
import { decryptId, encryptId } from "@/utils/utilsMethods";
import { skipToken } from "@reduxjs/toolkit/query";
import { Button, message, Segmented, Skeleton } from "antd";
import clsx from "clsx";
import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  transformDriverData,
  transformFleetData,
} from "../data/data-transform";
import { useDriverData } from "../hooks/useDriverData";
import { useFleetData } from "../hooks/useFleetData";
import CompanyHeaderInfo from "./CompanyHeaderInfo";
import ConsultandSelect from "./ConsultandSelect";
import ContinueOrSaveButton from "./ContinueOrSaveButton";
import MissionSelect from "./MissionSelect";

const PesvNoteAndSegmented = lazy(() => import("./PesvNoteAndSegmented"));

interface Props extends Partial<ConfigComun> {
  companyId: number;

  onlyInfo?: boolean;
  isOutOfContext?: boolean;
}
export default function CompanyInfo({
  companyId,
  onlyInfo,
  isOutOfContext = false,
  isExternal,
}: Props) {
  const { corporateId } = useCorporate();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const current = useAppSelector((state) => state.util.auditCurrent);
  const stepsLenght = useAppSelector((state) => state.util.stepLenght);
  const isCompanyInfoCollapse = useAppSelector(
    (state) => state.util.companyInfoCollapse,
  );
  const [searchParams] = useSearchParams();
  const auditParam = searchParams.get("audit");
  const auditId = auditParam ? parseInt(decryptId(auditParam)) : undefined;

  useEffect(() => {
    if (auditId === undefined) {
      dispatch(setAuditCurrent(0));
    }
  }, [auditId, dispatch]);

  const { fleetByCompany, isLoadingFleetByCompany } = useFleetData(
    companyId,
    auditId || 0,
  );

  const { driverByCompanyid, isLoadingDriverByCompany } = useDriverData(
    companyId,
    auditId || 0,
  );

  const fleetData = useMemo(() => {
    return fleetByCompany ? transformFleetData(fleetByCompany) : [];
  }, [fleetByCompany]);

  const driverCountData = useMemo(() => {
    return driverByCompanyid ? transformDriverData(driverByCompanyid) : [];
  }, [driverByCompanyid]);

  // 🔥 Actualizamos Redux SOLO cuando los datos realmente están listos
  useEffect(() => {
    if (!isLoadingFleetByCompany && fleetData.length > 0) {
      dispatch(setFleetData(fleetData));
    }

    if (!isLoadingDriverByCompany && driverCountData.length > 0) {
      dispatch(setDriverData(driverCountData));
    }
  }, [
    fleetData,
    driverCountData,
    isLoadingFleetByCompany,
    isLoadingDriverByCompany,
  ]);

  //#region states
  const [company, setCompany] = useState<Company | null>(null);
  const [consultorSelect, setConsultorSelect] = useState<number | null>(null);
  const [missionSelect, setMissionSelect] = useState<number | null>(null);
  const [size, setSize] = useState<number>(0);
  const [observationChanged, setObservationChanged] = useState<string | null>(
    null,
  );
  const [ejecution, setEjecution] = useState<string>("presencial");
  const [userChanged, setUserChanged] = useState<boolean>(false);
  //#endregion

  //#region querys
  const { data: corporateData } = corporateGroupService.useFindByIdQuery(
    corporateId ? { id: corporateId } : skipToken,
  );

  const { data, refetch, isUninitialized } = companyService.useFindByIdQuery(
    companyId ? { id: companyId } : skipToken,
  );
  const {
    data: auditDataById,
    refetch: refetchAudit,
    isUninitialized: unitializedAudit,
  } = auditService.useFindByIdQuery(
    auditId
      ? { auditId }
      : corporateId
        ? { auditId: 0, corporate_group: corporateId }
        : skipToken,
  );
  //#endregion

  //#region mutations
  const [saveAnswerCuestions, { isLoading }] =
    auditService.useSaveAnswerCuestionsMutation();
  // TODO: PENDIENTE ACTUALIZAR
  const [saveCountForCompanyInCorporate, { isLoading: corporateLoading }] =
    auditService.useSaveCountForCompanyInCorporateMutation();
  // TODO: PENDIENTE ACTUALIZAR
  const [saveAudit, { isLoading: saveProgressLoad }] =
    auditService.useSaveAuditMutation();
  // TODO: PENDIENTE ACTUALIZAR
  const [updateAudit, { isLoading: updateAuditLoading }] =
    auditService.useUpdateAuditMutation();

  const [updateCompany] = companyService.useUpdateCompanyMutation();
  //#endregion

  //Storage
  const totalVehicles = useAppSelector(
    (state) => state.vehicleQuestion.totalQuantity,
  );
  const totalDrivers = useAppSelector(
    (state) => state.driverQuestion.totalQuantity,
  );

  useEffect(() => {
    if (data) setCompany(data);
  }, [data]);

  useEffect(() => {
    if (auditDataById) {
      if (!isOutOfContext) {
        setSize(auditDataById.audit.type?.id ?? 0);
      }
      setObservationChanged(auditDataById.audit.observation);
      setConsultorSelect(auditDataById.audit.consultant?.id ?? null);
      setEjecution(auditDataById.audit.mode_ejecution ?? "presencial");
    }
  }, [auditDataById]);

  useEffect(() => {
    if (!isUninitialized) refetch();
  }, [isUninitialized, refetch]);

  useEffect(() => {
    if (!unitializedAudit) refetchAudit();
  }, [unitializedAudit, refetchAudit]);

  useEffect(() => {
    if (company && company.mission) setMissionSelect(company.mission.id);
  }, [company]);

  const onMissionChange = (missionId: number) => {
    setMissionSelect(missionId);
    try {
      updateCompany({
        id: companyId,
        mission: missionId,
      });
    } catch (error: any) {
      toastHandler(TOAST_TYPE.ERROR_TOAST, error.data.error);
    }
  };

  const auditData = useAppSelector((state) => state.audit.auditData);

  const saveProgressInAudit = async () => {
    try {
      const result = await saveAudit({
        company: companyId,
        auditDto: auditData,
        auditRequirementDto: auditRequirementData,
        consultor: consultorSelect ?? 0,
        audit: auditId ?? 0,
        mode_ejecution: ejecution,
        move_step: false,
      }).unwrap();

      // Se sincronizan los IDs del checklist despues del primer guardado
      if (
        result.checklist_requirements?.length > 0 ||
        result.checklist_questions?.length > 0
      ) {
        dispatch(
          syncChecklistIds({
            checklist_requirements: result.checklist_requirements,
            checklist_questions: result.checklist_questions,
          }),
        );
      }

      message.success("Progreso guardado correctamente");
    } catch (error) {
      message.error(
        "Error interno del sistema, comuniquese con un Administrador",
      );
    }
  };

  const confirm = async () => {
    try {
      switch (current) {
        case 0: {
          if (!auditDataById) {
            message.error("No se encontraron datos de la auditoría");
            return;
          }
          const data = await saveAnswerCuestions({
            auditId: auditDataById.audit?.id,
            // company: companyId,
            fleet_count: vehicleData,
            driver_count: driverData,
            // consultor: consultorSelect ?? 0,
          }).unwrap();
          message.success("Conteo Actualizado correctamente");
          navigate(
            `/app/companies/audits/${encryptId(
              companyId.toString(),
            )}?audit=${encryptId(data.audit_count.audit.toString())}`,
          );
          if (auditDataById) dispatch(setNextAuditCurrent());
          break;
        }
        case 1:
          dispatch(setCheckAuditDataForChanges()); // ! REVISAR
          await saveAudit({
            company: companyId,
            auditDto: auditData,
            auditRequirementDto: auditRequirementData,
            consultor: consultorSelect ?? 0,
            audit: auditId ?? 0,
            mode_ejecution: ejecution,
            move_step: true,
          }).unwrap();
          dispatch(setNextAuditCurrent());
          message.success("Lista de chequeo guardad :)");
          break;
      }
    } catch (error: any) {
      // Mostrar mensaje de error
      //(error);
      message.error(
        "Error interno del sistema, comuniquese con un Administrador",
      );
    }
  };
  const handleUpdateDataOfAudit = async () => {
    try {
      if (!isOutOfContext) {
        await updateAudit({
          id: auditDataById?.audit.id ?? 0,
          type: size,
          observation: observationChanged,
        }).unwrap();

        // Se limpian los IDs de checklist antes del refetch (volver a IDs maestros)
        dispatch(fullResetAudit());

        // ejecutará después de que updateAudit termine
        refetchAudit();
        setUserChanged(false);
      } else if (isExternal) {
        await saveAnswerCuestions({
          // company: companyId,
          fleet_count: vehicleData,
          driver_count: driverData,
          // consultor: consultorSelect ?? 0,
          external_count_complete: true,
        }).unwrap();
        dispatch(setNextExternalCurrent());
        message.success("Conteo Actualizado correctamente");
      } else {
        await saveCountForCompanyInCorporate({
          company: companyId,
          consultor: consultorSelect ?? 0,
          corporate: corporateId ?? 0,
          driverData,
          vehicleData,
        });
        message.success("Conteo Actualizado correctamente");
      }
    } catch (error) {
      message.error(
        "Error interno del sistema, comuníquese con un Administrador",
      );
    }
  };

  const vehicleData = useAppSelector(
    (state) => state.vehicleQuestion.fleetData,
  );
  const driverData = useAppSelector((state) => state.driverQuestion.driverData);

  const auditRequirementData = useAppSelector(
    (state) => state.audit.auditRequirementData,
  );

  const totalGeneral = totalDrivers + totalVehicles;

  const existCompany = companyId ? true : false;
  const handleSegmentChange = (newEjecution: string) => {
    setEjecution(newEjecution); // Mark that the user has manually changed the segment
  };
  const renderConsultantAndPESV = () => (
    <>
      {!isOutOfContext && (
        <div className="flex flex-col w-full col-span-6 gap-4 mt-4">
          <MissionSelect
            missionSelect={missionSelect}
            setMissionSelect={onMissionChange}
          />
          <ConsultandSelect
            consultorSelect={consultorSelect}
            setConsultorSelect={setConsultorSelect}
          />
          <Segmented
            options={[
              {
                label: "Presencial",
                value: "presencial",
              },
              {
                label: "Virtual",
                value: "virtual",
              },
            ]}
            value={ejecution}
            block
            className="col-span-6"
            onChange={handleSegmentChange}
          />
        </div>
      )}
      {existCompany && (
        <Suspense
          fallback={
            <>
              <div className="flex w-full">
                <Skeleton className="p-4 w-full" />
              </div>
            </>
          }
        >
          <PesvNoteAndSegmented
            size={size}
            setSize={setSize}
            userChanged={userChanged}
            observationChanged={observationChanged}
            setObservationChanged={setObservationChanged}
            setUserChanged={setUserChanged}
            missionId={missionSelect}
            is_in_count={current == 0 || isOutOfContext ? true : false}
            isExternal={isExternal}
          />
        </Suspense>
      )}
      {current > 0 && (
        <Button
          className="col-span-6"
          htmlType="button"
          onClick={saveProgressInAudit}
          loading={saveProgressLoad}
        >
          Guardar Progreso
        </Button>
      )}
      <ContinueOrSaveButton
        userChanged={userChanged}
        totalGeneral={totalGeneral}
        consultorSelect={consultorSelect}
        isLoading={isLoading}
        confirm={confirm}
        updateAuditLoading={updateAuditLoading}
        handleUpdateDataOfAudit={handleUpdateDataOfAudit}
        isOutOfContext={isOutOfContext}
        corporateLoading={corporateLoading}
        isExternal={isExternal}
      />
    </>
  );

  return (
    <div
      className={clsx(
        "grid grid-cols-6 gap-2 md:sticky top-2 w-full md:w-[30%] md:ml-8 relative transition",
        isCompanyInfoCollapse && "!w-0 overflow-hidden !ml-0 transition",
      )}
    >
      {current > 1 && (
        <Button
          className="col-span-6"
          htmlType="button"
          color="cyan"
          danger
          onClick={() => dispatch(setPrevAuditCurrent())}
        >
          Atras
        </Button>
      )}
      <CompanyHeaderInfo
        company={company}
        corporate_group={corporateData}
        isOutOfContext={isOutOfContext}
        isExternal={isExternal}
      />
      {isLoadingFleetByCompany ||
        (isLoadingDriverByCompany && <Skeleton className="w-40 h-60" />)}
      {isOutOfContext && !onlyInfo
        ? renderConsultantAndPESV()
        : current < stepsLenght - 1 && renderConsultantAndPESV()}
    </div>
  );
}
